import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Pricing.css';

const Pricing = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const plansRes = await api.get('/payments/plans');
        setPlans(plansRes.data);
        if (user) {
          const subRes = await api.get('/payments/my-subscription').catch(() => ({ data: null }));
          setCurrentSub(subRes.data);
        }
      } catch (err) {
        setError('Failed to load plans');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleSubscribe = async (planId) => {
    if (!user) {
      window.location.href = '/lms/login';
      return;
    }
    setSubscribing(planId);
    setError('');
    try {
      const res = await api.post('/payments/create-subscription', { plan_id: planId });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start checkout');
      setSubscribing(null);
    }
  };

  const getFeatures = (features) => {
    if (!features) return [];
    if (Array.isArray(features)) return features;
    if (typeof features === 'object') return Object.values(features);
    return [];
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page pricing-page">
      <div className="container">
        <section className="pricing-hero">
          <p className="eyebrow">Subscriptions</p>
          <h1>Choose Your Plan</h1>
          <p className="pricing-subtitle">
            Unlock unlimited access to all courses with a monthly subscription.
          </p>
        </section>

        {searchParams.get('subscribed') === '1' && (
          <div className="success-banner" role="status" aria-live="polite">
            Subscription activated! You now have full access to the platform.
          </div>
        )}

        {error && <div className="error-message" role="alert">{error}</div>}

        {currentSub && (
          <div className="current-plan-banner">
            Active plan: <strong>{currentSub.name}</strong> — expires{' '}
            {new Date(currentSub.expires_at).toLocaleDateString()}
          </div>
        )}

        {plans.length === 0 ? (
          <div className="no-plans">
            <p>No subscription plans available yet.</p>
            <Link to="/courses" className="btn-primary">Browse Courses</Link>
          </div>
        ) : (
          <div className="plans-grid" role="list">
            {plans.map((plan) => {
              const features = getFeatures(plan.features);
              const isActive = currentSub?.plan_id === plan.id;
              return (
                <article
                  key={plan.id}
                  className={`plan-card ${plan.is_featured ? 'plan-featured' : ''} ${isActive ? 'plan-active' : ''}`}
                  role="listitem"
                  aria-label={`${plan.name} plan, $${plan.price} per month`}
                >
                  {plan.is_featured && <span className="featured-badge">Most Popular</span>}
                  {isActive && <span className="active-badge">Current Plan</span>}
                  <div className="plan-header">
                    <h2 className="plan-name">{plan.name}</h2>
                    <div className="plan-price">
                      <span className="price-amount">${plan.price}</span>
                      <span className="price-period">/month</span>
                    </div>
                    {plan.description && <p className="plan-desc">{plan.description}</p>}
                  </div>

                  {features.length > 0 && (
                    <ul className="plan-features" aria-label="Plan features">
                      {features.map((f, i) => (
                        <li key={i}>
                          <span className="feature-check" aria-hidden="true">✓</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    className={`plan-btn ${plan.is_featured ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing === plan.id || isActive}
                    aria-label={isActive ? `Already subscribed to ${plan.name}` : `Subscribe to ${plan.name} for $${plan.price} per month`}
                  >
                    {isActive ? 'Active' : subscribing === plan.id ? 'Loading...' : 'Get Started'}
                  </button>
                </article>
              );
            })}
          </div>
        )}

        <section className="pricing-faq">
          <h2>Common Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Can I cancel anytime?</h3>
              <p>Yes. Cancel at any time and your access continues until the end of the billing period.</p>
            </div>
            <div className="faq-item">
              <h3>What's included?</h3>
              <p>All published courses on the platform, certificates, quizzes, and progress tracking.</p>
            </div>
            <div className="faq-item">
              <h3>Is there a free option?</h3>
              <p>Yes — many courses are free to enroll. Subscription unlocks premium content.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Pricing;
